import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { auth as authApi } from '@/api/auth';
import { ApiError } from '@/api/client';
import ErrorBanner from '@/components/ErrorBanner';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (!token) {
    return (
      <div className="centered-full login-card">
        <div className="login-inner">
          <h1>Invalid reset link</h1>
          <p className="muted">The link is missing its token. Request a new one.</p>
          <Link to="/forgot-password" className="btn btn-ghost">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  const mismatched = confirm.length > 0 && confirm !== password;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(new Error('Passwords do not match.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await authApi.resetPassword(token, password);
      navigate('/login?reset=ok', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError(new Error('This reset link is invalid or has expired.'));
      } else {
        setError(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="centered-full login-card">
      <div className="login-inner">
        <h1>Choose a new password</h1>
        <form className="form-grid" onSubmit={handleSubmit}>
          {error ? <ErrorBanner error={error} /> : null}
          <label>
            <span>New password (min 8 chars)</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label>
            <span>Confirm password</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
          {mismatched ? <span className="muted">Passwords don&apos;t match.</span> : null}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || password.length < 8 || password !== confirm}
          >
            {submitting ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
      </div>
    </div>
  );
}
